/// <reference types="@types/google.maps" />
import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import { throttle } from "lodash";
import { PlaceResult, EventLocation } from "types";

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}

interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}

interface PlaceType {
  place_id: string;
  description: string;
  structured_formatting: StructuredFormatting;
  isDbLocation?: boolean;
  dbLocationRef?: EventLocation;
}

const autocompleteService = {
  current: null as google.maps.places.AutocompleteService | null,
};

/**
 * Extracts a specific address component type from the Google Places result.
 */
function getAddressComponent(
  components: google.maps.GeocoderAddressComponent[],
  type: string,
  useShortName = false
): string {
  const comp = components.find((c) => c.types.includes(type));
  if (!comp) return "";
  return useShortName ? comp.short_name : comp.long_name;
}

function parsePlaceDetails(
  place: google.maps.places.PlaceResult
): PlaceResult | null {
  if (!place.geometry?.location || !place.address_components) return null;
  const components = place.address_components;
  return {
    place_id: place.place_id ?? "",
    name: place.name ?? "",
    formatted_address: place.formatted_address ?? "",
    route: getAddressComponent(components, "route"),
    street_number: getAddressComponent(components, "street_number"),
    city:
      getAddressComponent(components, "locality") ||
      getAddressComponent(components, "administrative_area_level_3") ||
      "N/A",
    province:
      getAddressComponent(components, "administrative_area_level_2", true) ||
      "N/A",
    region: getAddressComponent(components, "administrative_area_level_1") || "N/A",
    country: getAddressComponent(components, "country") || "N/A",
    postal_code: getAddressComponent(components, "postal_code") || "00000",
    latitude: place.geometry.location.lat(),
    longitude: place.geometry.location.lng(),
  };
}

interface GooglePlacesAutocompleteProps {
  value: PlaceResult | null;
  onChange: (value: PlaceResult | null) => void;
  error?: boolean;
  name?: string;
  onBlur?: React.FocusEventHandler;
  dbLocations?: EventLocation[];
}

const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  value,
  onChange,
  error,
  name,
  onBlur,
  dbLocations,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const [selectedOption, setSelectedOption] = useState<PlaceType | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(!!window.google?.maps);
  const serviceContainerRef = useRef<HTMLDivElement | null>(null);

  const mappedDbLocations = useMemo(() => {
    return (dbLocations || []).map((loc) => ({
      place_id: loc.id,
      description: loc.description,
      isDbLocation: true,
      dbLocationRef: loc,
      structured_formatting: {
        main_text: loc.description,
        secondary_text: loc.address,
      },
    }));
  }, [dbLocations]);

  // Dynamically load the Google Maps JS API (with Places library) once.
  useEffect(() => {
    const SCRIPT_ID = "google-maps-script";
    if (document.getElementById(SCRIPT_ID)) {
      // Script tag already present — might already be loaded or loading.
      if (window.google?.maps) setScriptLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptLoaded(true);
    document.head.appendChild(script);
  }, []);

  const fetch = useMemo(
    () =>
      throttle(
        (
          request: google.maps.places.AutocompletionRequest,
          callback: (
            results: google.maps.places.AutocompletePrediction[] | null
          ) => void
        ) => {
          autocompleteService.current!.getPlacePredictions(request, callback);
        },
        350
      ),
    []
  );

  // Reset or sync displayed option when external value is cleared or loaded.
  useEffect(() => {
    if (!value) {
      setSelectedOption(null);
      setInputValue("");
    } else {
      const isDb = !!dbLocations?.find((l) => l.id === value.place_id);
      setSelectedOption({
        place_id: value.place_id,
        description: value.name || value.formatted_address,
        isDbLocation: isDb,
        structured_formatting: {
          main_text: value.name || value.formatted_address,
          secondary_text: value.formatted_address,
        },
      });
      setInputValue(value.name || value.formatted_address);
    }
  }, [value, dbLocations]);

  useEffect(() => {
    let active = true;

    if (!scriptLoaded) return undefined;

    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      const defaultOptions = mappedDbLocations;
      const opts = selectedOption
        ? [
            selectedOption,
            ...defaultOptions.filter((o: PlaceType) => o.place_id !== selectedOption.place_id),
          ]
        : defaultOptions;
      setOptions(opts);
      return undefined;
    }

    if (inputValue.length < 3) {
      // Don't search Google until we have at least 3 characters.
      setOptions(selectedOption ? [selectedOption] : []);
      return undefined;
    }

    fetch(
      { input: inputValue },
      (results: google.maps.places.AutocompletePrediction[] | null) => {
        if (active) {
          let newOptions: readonly PlaceType[] = [];
          if (selectedOption) {
            newOptions = [selectedOption];
          }
          if (results) {
            newOptions = [...newOptions, ...results];
          }
          setOptions(newOptions);
        }
      }
    );

    return () => {
      active = false;
    };
  }, [selectedOption, inputValue, mappedDbLocations, scriptLoaded, fetch]);

  const handleChange = (
    _: React.SyntheticEvent,
    newValue: PlaceType | null
  ) => {
    setOptions(newValue ? [newValue, ...options] : options);
    setSelectedOption(newValue);

    if (!newValue) {
      onChange(null);
      return;
    }

    if (newValue.isDbLocation && newValue.dbLocationRef) {
      const loc = newValue.dbLocationRef;
      onChange({
        place_id: loc.id,
        name: loc.description,
        formatted_address: loc.address,
        route: "N/A",
        street_number: "N/A",
        city: loc.city,
        province: loc.province,
        region: loc.region,
        country: loc.country,
        postal_code: loc.zip || "00000",
        latitude: 0,
        longitude: 0,
      });
      return;
    }

    if (!serviceContainerRef.current) {
      serviceContainerRef.current = document.createElement("div");
    }

    const placesService = new window.google.maps.places.PlacesService(
      serviceContainerRef.current
    );

    placesService.getDetails(
      {
        placeId: newValue.place_id,
        fields: [
          "place_id",
          "name",
          "formatted_address",
          "address_components",
          "geometry",
        ],
      },
      (
        placeDetail: google.maps.places.PlaceResult | null,
        status: google.maps.places.PlacesServiceStatus
      ) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          placeDetail
        ) {
          onChange(parsePlaceDetails(placeDetail));
        }
      }
    );
  };

  return (
    <Autocomplete
      id={name ?? "google-places-autocomplete"}
      size="small"
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={selectedOption}
      noOptionsText="No locations"
      onChange={handleChange}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          onBlur={onBlur}
          error={error}
          size="small"
          fullWidth
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props as typeof props & { key: string };
        const matches =
          option.structured_formatting.main_text_matched_substrings || [];
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match: MainTextMatchedSubstrings) => [
            match.offset,
            match.offset + match.length,
          ])
        );

        return (
          <li key={key} {...optionProps}>
            <Grid container sx={{ alignItems: "center" }}>
              <Grid item sx={{ display: "flex", width: 44 }}>
                <LocationOnIcon sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid
                item
                sx={{ width: "calc(100% - 44px)", wordBreak: "break-word" }}
              >
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                  >
                    {part.text}
                  </Box>
                ))}
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
};

export default GooglePlacesAutocomplete;
